import React from "react";
import { Accordion, AccordionDetails, AccordionSummary, Stack, Typography } from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import typography from "../cssUtils/typography";

const formatDate = (iso) => {
  if (!iso) return "not yet";
  try {
    return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

const Section = ({ title, children }) => (
  <Stack spacing={0.5}>
    <Typography sx={{ ...typography.bodyLarge, fontWeight: 600 }}>{title}</Typography>
    {children}
  </Stack>
);

const Line = ({ children }) => (
  <Typography sx={{ ...typography.bodySmall, color: "text.secondary" }}>{children}</Typography>
);

/**
 * "How this is calculated": where prices come from, the formula, what is (not) counted.
 * Shared by the AI Token Calculator and AI Bot Analytics.
 *
 * prices: [{ label, input_per_mtok, output_per_mtok, effective_from }]
 * steps:  extra lines describing what a question is made of (calculator) or how
 *         recorded costs work (analytics)
 */
const CostExplainer = ({
  title = "How this is calculated",
  priceSource,
  pricesLastUpdated,
  prices = [],
  formula,
  steps = [],
  wordsNote,
  usdToInrRate,
  exclusions = [],
  defaultExpanded = false,
}) => (
  <Accordion defaultExpanded={defaultExpanded} disableGutters sx={{ width: "100%", borderRadius: 2, "&:before": { display: "none" } }}>
    <AccordionSummary expandIcon={<ExpandMore />}>
      <Typography sx={{ ...typography.bodyLarge, fontWeight: 600 }}>{title}</Typography>
    </AccordionSummary>
    <AccordionDetails>
      <Stack spacing={2}>
        <Section title="Where prices come from">
          <Line>{priceSource}</Line>
          <Line>Prices last updated: {formatDate(pricesLastUpdated)}</Line>
          {prices.map((price) => (
            <Line key={price.label}>
              {price.label}: ${price.input_per_mtok ?? "?"} per 1M input tokens
              {price.output_per_mtok ? `, $${price.output_per_mtok} per 1M output tokens` : ""}
              {price.effective_from ? ` (since ${formatDate(price.effective_from)})` : ""}
            </Line>
          ))}
        </Section>
        {formula && (
          <Section title="Formula">
            <Line>{formula}</Line>
          </Section>
        )}
        {steps.length > 0 && (
          <Section title="What is counted">
            {steps.map((step) => (
              <Line key={step}>• {step}</Line>
            ))}
          </Section>
        )}
        {(wordsNote || usdToInrRate) && (
          <Section title="Conversions">
            {wordsNote && <Line>{wordsNote}</Line>}
            {usdToInrRate && <Line>Exchange rate used: 1 USD = ₹{usdToInrRate}</Line>}
          </Section>
        )}
        {exclusions.length > 0 && (
          <Section title="Not included">
            {exclusions.map((item) => (
              <Line key={item}>• {item}</Line>
            ))}
          </Section>
        )}
      </Stack>
    </AccordionDetails>
  </Accordion>
);

export default CostExplainer;
